import { useRef, useCallback, useEffect } from 'react';

interface AsyncTask<T = any> {
  id: string;
  execute: () => Promise<T>;
  cleanup?: () => Promise<void>;
  cancelled: boolean;
  timestamp: number;
}

interface UseAsyncQueueOptions {
  /**
   * 是否在新任务到来时自动取消等待中的任务
   * @default true
   */
  cancelPending?: boolean;

  /**
   * 是否在执行新任务前先执行清理操作
   * @default true
   */
  cleanupBeforeNew?: boolean;

  /**
   * 调试模式，会输出日志
   * @default false
   */
  debug?: boolean;
}

const generateTaskId = () => {
  return `task_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

export function useAsyncQueue(options: UseAsyncQueueOptions = {}) {
  const { cancelPending = true, cleanupBeforeNew = true, debug = false } = options;

  const queue = useRef<AsyncTask[]>([]);
  const currentTask = useRef<AsyncTask | null>(null);
  const cleanupTask = useRef<AsyncTask | null>(null);
  const isProcessing = useRef(false);
  const closed = useRef(false);

  const log = useCallback(
    (...args: any[]) => {
      if (debug) {
        console.log('[AsyncQueue]', ...args);
      }
    },
    [debug]
  );

  const processQueue = useCallback(async () => {
    if (isProcessing.current) {
      return;
    }

    isProcessing.current = true;

    while (queue.current.length > 0 && !closed.current) {
      // 找到最新的未取消任务
      let nextTask: AsyncTask | null = null;

      if (cancelPending) {
        // 取消所有等待中的任务，只保留最新的
        for (let i = 0; i < queue.current.length - 1; i++) {
          const task = queue.current[i];
          if (task !== currentTask.current) {
            task.cancelled = true;
            log(`取消等待任务: ${task.id}`);
          }
        }

        // 找最新的未取消任务
        for (let i = queue.current.length - 1; i >= 0; i--) {
          const task = queue.current[i];
          if (!task.cancelled) {
            nextTask = task;
            break;
          }
        }
      } else {
        // 按顺序执行，找到第一个未取消的任务
        nextTask = queue.current.find(task => !task.cancelled) || null;
      }

      // 清理已取消的任务
      queue.current = queue.current.filter(task => !task.cancelled);

      if (!nextTask) {
        break;
      }

      // 如果需要清理且当前有正在执行的任务
      if (cleanupBeforeNew && currentTask.current && currentTask.current !== nextTask) {
        log(`检测到新任务，开始清理当前任务: ${currentTask.current.id}`);

        if (currentTask.current.cleanup) {
          try {
            await currentTask.current.cleanup();
            log(`清理完成: ${currentTask.current.id}`);
          } catch (error) {
            log(`清理失败: ${currentTask.current.id}`, error);
          }
        }

        currentTask.current = null;
        cleanupTask.current = null;
      } else if (cleanupTask.current && cleanupTask.current.cleanup) {
        // 清理上一个任务的清理任务
        await cleanupTask.current.cleanup();
        log(`清理完成: ${cleanupTask.current.id}`);
        cleanupTask.current = null;
      }

      // 执行新任务
      if (!nextTask.cancelled) {
        currentTask.current = nextTask;

        try {
          log(`开始执行任务: ${nextTask.id}`);

          await nextTask.execute();

          if (!nextTask.cancelled) {
            log(`任务执行完成: ${nextTask.id}`);
            if (nextTask.cleanup) {
              cleanupTask.current = nextTask;
            }
          } else {
            log(`任务被取消: ${nextTask.id}`);
          }
        } catch (error) {
          const err = error as Error;
          log(`任务执行失败: ${nextTask.id}`, err);
        }

        // 从队列中移除已处理的任务
        queue.current = queue.current.filter(task => task !== nextTask);
      }
    }

    currentTask.current = null;
    isProcessing.current = false;
    log('队列处理完成');
  }, [cancelPending, cleanupBeforeNew, log]);

  const enqueue = useCallback(
    async <T = any>(taskExecutor: () => Promise<T>, cleanupExecutor?: () => Promise<void>): Promise<T> => {
      return new Promise((resolve, reject) => {
        const taskId = generateTaskId();

        const task: AsyncTask<T> = {
          id: taskId,
          cancelled: false,
          timestamp: Date.now(),
          execute: async () => {
            try {
              const result = await taskExecutor();
              if (!task.cancelled) {
                resolve(result);
              }
              return result;
            } catch (error) {
              if (!task.cancelled) {
                reject(error);
              }
              throw error;
            }
          },
          cleanup: cleanupExecutor,
        };

        queue.current.push(task);
        log(`任务入队: ${taskId}, 队列长度: ${queue.current.length}`);

        // 开始处理队列
        processQueue();
      });
    },
    [processQueue, log]
  );

  useEffect(() => {
    return () => {
      enqueue(() => {
        return new Promise(resolve => {
          setTimeout(resolve, 1000);
        });
      });
    };
  }, []);

  const cancelAll = useCallback(() => {
    queue.current.forEach(task => {
      task.cancelled = true;
    });
    queue.current = [];
    log('所有任务已取消');
  }, [log]);

  return {
    enqueue,
    cancelAll,
  };
}
